<template>
  <v-card class="foro-curso d-flex flex-column" height="100%">
    <v-toolbar color="primary">
      <v-btn icon="mdi-arrow-left" :aria-label="t('Foro_tancar')" @click="$emit('cerrar')" />
      <v-toolbar-title>{{ t('Foro_titol') }}</v-toolbar-title>
      <v-btn icon="mdi-refresh" :aria-label="t('Foro_actualitzar')"
        :loading="carregant" @click="actualitzar" />
      <v-btn icon="mdi-close" :aria-label="t('Foro_tancar')" @click="$emit('cerrar')" />
    </v-toolbar>

    <v-card-text class="foro-contingut flex-grow-1">
      <v-alert v-if="error" class="mb-4" type="error" variant="tonal"
        closable @click:close="error = ''">{{ error }}</v-alert>

      <template v-if="filActual">
        <v-btn variant="text" prepend-icon="mdi-chevron-left" class="mb-3"
          @click="tornarFils">{{ t('Foro_tornar') }}</v-btn>
        <h2 class="text-h5 mb-1">{{ filActual.titulo }}</h2>
        <p class="text-caption mb-4">{{ t('Foro_iniciat_per') }} {{ filActual.autor_nom }}</p>
        <v-progress-linear v-if="carregant" indeterminate color="primary" />

        <v-card v-for="missatge in filActual.mensajes" :key="missatge.id"
          variant="outlined" class="mb-3">
          <v-card-item>
            <v-card-title class="text-body-1 font-weight-bold text-wrap">
              {{ missatge.autor_nom }}
            </v-card-title>
            <v-card-subtitle>
              {{ formatData(missatge.creado_el) }}
              <span v-if="missatge.responde_a_id">
                · {{ t('Foro_resposta_a') }}
                {{ nomPare(missatge.responde_a_id) }}
              </span>
            </v-card-subtitle>
          </v-card-item>
          <v-card-text class="foro-text">{{ missatge.texto }}</v-card-text>
          <v-card-actions>
            <v-btn size="small" variant="text" prepend-icon="mdi-reply"
              @click="seleccionarResposta(missatge)">
              {{ t('Foro_respondre') }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card variant="tonal" class="mt-5">
          <v-card-title class="text-wrap">{{ t('Foro_escriure_resposta') }}</v-card-title>
          <v-card-text>
            <div v-if="responA" class="d-flex align-center mb-2">
              <v-chip closable @click:close="responA = null">
                {{ t('Foro_resposta_a') }} {{ responA.autor_nom }}
              </v-chip>
            </div>
            <v-textarea v-model="textResposta" :label="t('Foro_missatge')"
              :maxlength="10000" counter rows="3" auto-grow variant="outlined" />
            <v-btn color="primary" :loading="enviant" :disabled="!textResposta.trim()"
              @click="publicarResposta">
              {{ t('Foro_publicar_resposta') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </template>

      <template v-else>
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
          <h2 class="text-h5">{{ t('Foro_fils') }}</h2>
          <v-btn prepend-icon="mdi-plus" color="primary" @click="crearVisible = true">
            {{ t('Foro_fil_nou') }}
          </v-btn>
        </div>
        <v-progress-linear v-if="carregant" indeterminate color="primary" class="mb-3" />
        <v-card v-if="!carregant && !fils.length" variant="tonal">
          <v-card-text>{{ t('Foro_sense_fils') }}</v-card-text>
        </v-card>
        <v-list v-else lines="three" rounded="lg" class="pa-0">
          <v-list-item v-for="fil in fils" :key="fil.id" class="mb-2 border rounded-lg"
            @click="obrirFil(fil.id)">
            <template #prepend>
              <v-icon color="primary" icon="mdi-forum-outline" class="mr-3" />
            </template>
            <v-list-item-title class="font-weight-bold text-wrap">
              {{ fil.titulo }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-wrap">
              {{ fil.autor_nom }} · {{ formatData(fil.actualizado_el) }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              {{ fil.num_mensajes }} {{ t('Foro_missatges') }}
            </v-list-item-subtitle>
            <template #append>
              <v-icon icon="mdi-chevron-right" />
            </template>
          </v-list-item>
        </v-list>
        <div v-if="paginaSeguent" class="text-center mt-3">
          <v-btn variant="tonal" :loading="carregant" @click="carregarFils(true)">
            {{ t('Foro_mes_fils') }}
          </v-btn>
        </div>
      </template>
    </v-card-text>

    <v-dialog v-model="crearVisible" max-width="650">
      <v-card>
        <v-card-title>{{ t('Foro_fil_nou') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="titolNou" :label="t('Foro_assumpte')"
            :maxlength="160" counter variant="outlined" />
          <v-textarea v-model="textNou" :label="t('Foro_missatge_inicial')"
            :maxlength="10000" counter rows="5" variant="outlined" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="crearVisible = false">{{ t('Foro_cancel_lar') }}</v-btn>
          <v-btn color="primary" :loading="enviant"
            :disabled="!titolNou.trim() || !textNou.trim()" @click="publicarFil">
            {{ t('Foro_publicar_fil') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { axios } from '../axios';

const props = defineProps({
  cursoId: { type: [Number, String], required: true },
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(['cerrar', 'novetats']);
const { t, locale } = useI18n();

const fils = ref([]);
const filActual = ref(null);
const paginaSeguent = ref(null);
const carregant = ref(false);
const enviant = ref(false);
const error = ref('');
const crearVisible = ref(false);
const titolNou = ref('');
const textNou = ref('');
const textResposta = ref('');
const responA = ref(null);

const ruta = () => 'foro/' + props.cursoId + '/';

function formatData(valor) {
  if (!valor) return '';
  return new Intl.DateTimeFormat(
    locale.value === 'ca' ? 'ca-ES' : locale.value === 'es' ? 'es-ES' : 'en-GB',
    { dateStyle: 'medium', timeStyle: 'short' }
  ).format(new Date(valor));
}

function missatgeError(err) {
  return err.response?.data?.autor?.[0]
    || err.response?.data?.detail
    || t('Foro_error');
}

async function carregarFils(mes = false) {
  if (carregant.value) return;
  carregant.value = true;
  error.value = '';
  try {
    const pagina = mes ? paginaSeguent.value : 1;
    const { data } = await axios.get(ruta(), { params: { page: pagina } });
    fils.value = mes ? [...fils.value, ...data.hilos] : data.hilos;
    paginaSeguent.value = data.pagina_siguiente;
  } catch (err) {
    error.value = missatgeError(err);
  } finally {
    carregant.value = false;
  }
}

async function marcarComLlegit() {
  try {
    const { data: estat } = await axios.get(ruta() + 'novedades/');
    const { data } = await axios.post(ruta() + 'leer/', {
      ultimo_mensaje_id: estat.ultimo_mensaje_id,
    });
    emit('novetats', data.no_leidos);
  } catch (err) {
    // No s'ha de bloquejar la lectura del fòrum si falla el marcador.
    console.error('No s’ha pogut actualitzar la lectura del fòrum', err);
  }
}

async function obrirFil(id) {
  carregant.value = true;
  error.value = '';
  try {
    const { data } = await axios.get(ruta() + 'hilos/' + id + '/');
    filActual.value = data;
    responA.value = null;
    textResposta.value = '';
    await marcarComLlegit();
  } catch (err) {
    error.value = missatgeError(err);
  } finally {
    carregant.value = false;
  }
}

async function actualitzar() {
  if (filActual.value) await obrirFil(filActual.value.id);
  else {
    await carregarFils();
    await marcarComLlegit();
  }
}

function tornarFils() {
  filActual.value = null;
  responA.value = null;
  carregarFils();
}

function nomPare(id) {
  return filActual.value?.mensajes.find(m => m.id === id)?.autor_nom || '';
}

function seleccionarResposta(missatge) {
  responA.value = missatge;
}

async function publicarFil() {
  if (!titolNou.value.trim() || !textNou.value.trim() || enviant.value) return;
  enviant.value = true;
  error.value = '';
  try {
    const { data } = await axios.post(ruta(), {
      titulo: titolNou.value.trim(), texto: textNou.value.trim(),
    });
    titolNou.value = '';
    textNou.value = '';
    crearVisible.value = false;
    filActual.value = null;
    await carregarFils();
    await obrirFil(data.id);
  } catch (err) {
    error.value = missatgeError(err);
    crearVisible.value = false;
  } finally {
    enviant.value = false;
  }
}

async function publicarResposta() {
  if (!textResposta.value.trim() || !filActual.value || enviant.value) return;
  enviant.value = true;
  error.value = '';
  const idFil = filActual.value.id;
  try {
    await axios.post(ruta() + 'hilos/' + idFil + '/mensajes/', {
      texto: textResposta.value.trim(),
      responde_a_id: responA.value?.id ?? null,
    });
    textResposta.value = '';
    responA.value = null;
    await obrirFil(idFil);
  } catch (err) {
    error.value = missatgeError(err);
  } finally {
    enviant.value = false;
  }
}

watch(() => props.visible, async (visible) => {
  if (visible) {
    filActual.value = null;
    await carregarFils();
    await marcarComLlegit();
  }
}, { immediate: true });
</script>

<style scoped>
.foro-contingut { overflow-y: auto; }
.foro-text { white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
